import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiPost } from "../../lib/api";
import { useTenantAuth } from "../../context/auth";
import { QrCodeImage } from "../../components/QrCodeImage";

type TelegramWebAppHandle = {
  initData?: string;
  initDataUnsafe?: { start_param?: string };
};

function getTelegramInitData() {
  const tg = (window as any)?.Telegram?.WebApp as TelegramWebAppHandle | undefined;
  return (tg?.initData as string | undefined) || "";
}

function getLaunchParamFromHash(key: string): string {
  const hash = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
  if (!hash) return "";
  const params = new URLSearchParams(hash);
  return params.get(key) || "";
}

function getTelegramStartParam() {
  const tg = (window as any)?.Telegram?.WebApp as TelegramWebAppHandle | undefined;
  return (
    tg?.initDataUnsafe?.start_param ||
    new URLSearchParams(window.location.search).get("startapp") ||
    new URLSearchParams(window.location.search).get("tgWebAppStartParam") ||
    getLaunchParamFromHash("tgWebAppStartParam") ||
    ""
  );
}

type ExchangeResponse = {
  signup_session_jwt: string;
  expires_in_sec: number;
  preset: {
    country_code: string;
    default_locale: string;
    default_channel_strategy: "telegram_first" | "whatsapp_first" | "email_first";
    default_currency: string;
    default_plan_code?: string;
    supported_locales?: string[];
    allowed_channel_strategies?: string[];
    requires_captcha?: boolean;
  };
};

type ByobPhase = "idle" | "validating" | "creating" | "webhook" | "test" | "done" | "error";

type ByobHistoryEntry = {
  at: string;
  phase: ByobPhase;
  message: string;
};

type BotFatherChecklist = {
  opened_botfather: boolean;
  copied_newbot_script: boolean;
  copied_commands_script: boolean;
  copied_menu_target: boolean;
  token_pasted: boolean;
};

type ByobUiStep = {
  id: string;
  label: string;
  done: boolean;
  active?: boolean;
};

type ByobRegionalPreset = {
  countryCode: string;
  label: string;
  assistantWord: string;
  commands: {
    dashboard: string;
    calendar: string;
    clients: string;
  };
  botFatherLanguageHint: string;
  menuButtonHint: string;
  setupNotes: string[];
  usernameCountrySuffix?: string;
  testMessage: string;
};

const DEFAULT_BYOB_REGION_PRESET: ByobRegionalPreset = {
  countryCode: "DEFAULT",
  label: "Global",
  assistantWord: "Assistant",
  commands: {
    dashboard: "Open Salon OS",
    calendar: "Open calendar",
    clients: "Open clients",
  },
  botFatherLanguageHint: "Use English command labels if your team works across multiple languages.",
  menuButtonHint: "Set the menu button to the manager Mini App entry so staff can reopen the dashboard quickly.",
  setupNotes: [
    "Create the bot in BotFather, then paste the token here for automated validation and webhook setup.",
    "Use a dedicated owner/admin bot token to avoid mixing personal and production automation traffic.",
  ],
  testMessage: "Salon OS BYOB setup complete.",
};

const BYOB_REGION_PRESETS: Record<string, ByobRegionalPreset> = {
  US: {
    countryCode: "US",
    label: "United States",
    assistantWord: "Assistant",
    commands: {
      dashboard: "Open Salon OS",
      calendar: "Open calendar",
      clients: "Open clients",
    },
    botFatherLanguageHint: "English labels are recommended for most US salons.",
    menuButtonHint: "Use the manager Mini App URL as the menu button target for quick staff re-entry.",
    setupNotes: [
      "Keep command names short and action-focused (dashboard/calendar/clients).",
      "Test-send to the owner chat after webhook sync to confirm production reachability.",
    ],
    testMessage: "Salon OS BYOB setup complete.",
  },
  BR: {
    countryCode: "BR",
    label: "Brazil",
    assistantWord: "Assistente",
    commands: {
      dashboard: "Abrir Salon OS",
      calendar: "Abrir agenda",
      clients: "Abrir clientes",
    },
    botFatherLanguageHint: "Portuguese labels improve staff adoption for bot menus and commands.",
    menuButtonHint: "Use the same Mini App URL and keep menu labels in Portuguese in BotFather steps.",
    setupNotes: [
      "Prefer Portuguese labels for operator-facing commands and onboarding copy.",
      "Validate webhook and send a test message before sharing the bot publicly with the team.",
    ],
    usernameCountrySuffix: "br",
    testMessage: "Configuracao BYOB do Salon OS concluida.",
  },
  NL: {
    countryCode: "NL",
    label: "Netherlands",
    assistantWord: "Assistent",
    commands: {
      dashboard: "Open Salon OS",
      calendar: "Open agenda",
      clients: "Open klanten",
    },
    botFatherLanguageHint: "Dutch/English mixed labels are common; choose one style and keep it consistent.",
    menuButtonHint: "Point the menu button to the Mini App URL and keep command descriptions short.",
    setupNotes: [
      "For multi-location teams, keep command names stable and localize only descriptions.",
      "Use a dedicated admin chat_id for initial test-send verification.",
    ],
    usernameCountrySuffix: "nl",
    testMessage: "Salon OS BYOB-installatie voltooid.",
  },
  ES: {
    countryCode: "ES",
    label: "Spain",
    assistantWord: "Asistente",
    commands: {
      dashboard: "Abrir Salon OS",
      calendar: "Abrir agenda",
      clients: "Abrir clientes",
    },
    botFatherLanguageHint: "Spanish labels are recommended for in-salon operator workflows.",
    menuButtonHint: "Set /setmenubutton to the manager Mini App URL and use Spanish copy in BotFather prompts.",
    setupNotes: [
      "Use clear Spanish descriptions for command discoverability.",
      "Run a test-send after webhook sync before onboarding staff.",
    ],
    usernameCountrySuffix: "es",
    testMessage: "Configuracion BYOB de Salon OS completada.",
  },
};

export function TelegramOnboardingPage() {
  const navigate = useNavigate();
  const { loginWithToken } = useTenantAuth();
  const [initData, setInitData] = useState("");
  const [country, setCountry] = useState("US");
  const [locale, setLocale] = useState((navigator.language || "en").slice(0, 2).toLowerCase());
  const [captchaToken, setCaptchaToken] = useState("");
  const [exchange, setExchange] = useState<ExchangeResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [tenantSlug, setTenantSlug] = useState("");
  const [branchName, setBranchName] = useState("Main");
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
  const [currency, setCurrency] = useState("USD");
  const [channelStrategy, setChannelStrategy] = useState<"telegram_first" | "whatsapp_first" | "email_first">("telegram_first");
  const [consent, setConsent] = useState(false);
  const [result, setResult] = useState<{ public_booking_url: string; onboarding_checklist: string[]; manager_jwt: string; manager_app_url: string; tenant_id: string } | null>(null);

  const [byobToken, setByobToken] = useState("");
  const [byobChatId, setByobChatId] = useState("");
  const [byobIntegrationId, setByobIntegrationId] = useState("");
  const [byobStatus, setByobStatus] = useState("");
  const [byobPhase, setByobPhase] = useState<ByobPhase>("idle");
  const [byobValidated, setByobValidated] = useState(false);
  const [byobWebhookSynced, setByobWebhookSynced] = useState(false);
  const [byobTestSent, setByobTestSent] = useState(false);
  const [byobHistory, setByobHistory] = useState<ByobHistoryEntry[]>([]);
  const [copyFeedback, setCopyFeedback] = useState("");
  const [botFatherChecklist, setBotFatherChecklist] = useState<BotFatherChecklist>({
    opened_botfather: false,
    copied_newbot_script: false,
    copied_commands_script: false,
    copied_menu_target: false,
    token_pasted: false,
  });
  const [commandScriptOverride, setCommandScriptOverride] = useState("");
  const [menuTargetOverride, setMenuTargetOverride] = useState("");

  useEffect(() => {
    const startParam = getTelegramStartParam();
    if (startParam && startParam.startsWith("onboard_")) {
      const rawToken = startParam.slice("onboard_".length);
      navigate(`/tg/onboard-owner?token=${encodeURIComponent(rawToken)}`, { replace: true });
      return;
    }

    const tgData = getTelegramInitData();
    if (tgData) setInitData(tgData);
    const pendingRaw = sessionStorage.getItem("pending_signup_session");
    if (pendingRaw) {
      try {
        const pending = JSON.parse(pendingRaw) as { signup_session_jwt?: string; preset?: ExchangeResponse["preset"] };
        if (pending.signup_session_jwt) {
          setExchange({ signup_session_jwt: pending.signup_session_jwt, expires_in_sec: 1800, preset: (pending.preset as any) ?? { country_code: "US", default_locale: "en", default_channel_strategy: "email_first", default_currency: "USD" } });
        }
      } catch {
        // ignore malformed session cache
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (!exchange) return;
    setCurrency(exchange.preset.default_currency || "USD");
    setChannelStrategy(exchange.preset.default_channel_strategy || "telegram_first");
    setLocale(exchange.preset.default_locale || "en");
    setCountry(exchange.preset.country_code || "US");
  }, [exchange]);

  const allowedStrategies = useMemo(
    () => exchange?.preset.allowed_channel_strategies ?? ["telegram_first", "whatsapp_first", "email_first"],
    [exchange]
  );
  const byobCountryCode = (exchange?.preset.country_code || country || "US").toUpperCase();
  const byobRegionPreset = BYOB_REGION_PRESETS[byobCountryCode] ?? DEFAULT_BYOB_REGION_PRESET;
  const signupQrValue = useMemo(() => `${window.location.origin}/signup`, []);
  const byobMenuTargetDefault = `${window.location.origin}/tg/manager`;

  const pushByobHistory = (phase: ByobPhase, message: string) => {
    setByobHistory((prev) => [{ at: new Date().toISOString(), phase, message }, ...prev].slice(0, 20));
  };

  const runExchange = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await apiPost<ExchangeResponse>(
        "/v3/public/signup/telegram/exchange",
        { telegram_init_data: initData, selected_country_code: country, locale, captcha_token: captchaToken || null },
        { auth: "none" }
      );
      setExchange(res);
      sessionStorage.setItem("pending_signup_session", JSON.stringify({ signup_session_jwt: res.signup_session_jwt, preset: res.preset, verification_method: "telegram" }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Exchange failed");
    } finally {
      setBusy(false);
    }
  };

  const completeSignup = async (e: FormEvent) => {
    e.preventDefault();
    if (!exchange) return;
    setBusy(true);
    setError("");
    try {
      const res = await apiPost<{
        tenant_id: string;
        tenant_slug: string;
        branch_slug: string;
        manager_jwt: string;
        manager_app_url: string;
        public_booking_url: string;
        onboarding_checklist: string[];
      }>(
        "/v3/public/signup/complete",
        {
          tenant_name: tenantName,
          tenant_slug: tenantSlug || null,
          default_branch_name: branchName,
          timezone,
          currency,
          channel_strategy: channelStrategy,
          consent: { terms_accepted: consent, privacy_accepted: consent, accepted_at: new Date().toISOString() },
        },
        { auth: "none", headers: { Authorization: `Bearer ${exchange.signup_session_jwt}` } }
      );
      loginWithToken(res.manager_jwt);
      setResult({ public_booking_url: res.public_booking_url, onboarding_checklist: res.onboarding_checklist, manager_jwt: res.manager_jwt, manager_app_url: res.manager_app_url, tenant_id: res.tenant_id });
      sessionStorage.removeItem("pending_signup_session");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Signup complete failed");
    } finally {
      setBusy(false);
    }
  };

  const createByobIntegration = async () => {
    if (!byobToken.trim()) return;
    setByobValidated(false);
    setByobWebhookSynced(false);
    setByobTestSent(false);
    setBotFatherChecklist((prev) => ({ ...prev, token_pasted: true }));
    pushByobHistory("idle", "Started BYOB auto-setup run");
    setByobPhase("validating");
    setByobStatus("Validating bot token...");
    try {
      pushByobHistory("validating", "Validating bot token");
      const validate = await apiPost<{ ok: boolean; details: Record<string, unknown> }>("/v3/integrations/telegram/validate-token", { bot_token: byobToken.trim() });
      if (!validate.ok) {
        setByobPhase("error");
        setByobStatus(`Validation failed: ${JSON.stringify(validate.details)}`);
        pushByobHistory("error", "Token validation failed");
        return;
      }
      setByobValidated(true);
      setByobPhase("creating");
      setByobStatus("Creating Telegram integration...");
      pushByobHistory("creating", "Creating integration");
      const created = await apiPost<{ id: string }>("/v3/integrations", {
        branch_id: null,
        channel: "telegram",
        name: "owner_bot",
        config: { bot_token: byobToken.trim() },
        enabled: true,
      });
      setByobIntegrationId(created.id);
      setByobPhase("webhook");
      setByobStatus("Syncing webhook...");
      pushByobHistory("webhook", `Syncing webhook for ${created.id}`);
      await apiPost(`/v3/integrations/${created.id}/telegram/webhook/sync`, {});
      setByobWebhookSynced(true);
      if (byobChatId.trim()) {
        setByobPhase("test");
        setByobStatus("Sending test message...");
        pushByobHistory("test", "Sending Telegram test message");
        await apiPost(`/v3/integrations/${created.id}/telegram/test-send`, { chat_id: byobChatId.trim(), message: byobRegionPreset.testMessage });
        setByobTestSent(true);
      }
      setByobPhase("done");
      setByobStatus(`BYOB setup completed. Integration ID: ${created.id}`);
      pushByobHistory("done", `BYOB setup completed (${created.id})`);
    } catch (e) {
      setByobPhase("error");
      setByobStatus(e instanceof Error ? e.message : "BYOB setup failed");
      pushByobHistory("error", e instanceof Error ? e.message : "BYOB setup failed");
    }
  };

  const markBotFatherStep = (key: keyof BotFatherChecklist, value = true) => {
    setBotFatherChecklist((prev) => ({ ...prev, [key]: value }));
  };

  const copyWithFeedback = async (text: string, onSuccess?: () => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback("Copied");
      onSuccess?.();
      window.setTimeout(() => setCopyFeedback(""), 1500);
    } catch {
      setCopyFeedback("Copy failed");
      window.setTimeout(() => setCopyFeedback(""), 1500);
    }
  };

  const botFatherDeepLink = "https://t.me/BotFather";
  const botNameSuggestion = `${(tenantName || "Salon").trim()} ${byobRegionPreset.assistantWord}`.slice(0, 60);
  const botUsernameSuggestion = `${((tenantSlug || tenantName || "salon").toLowerCase().replace(/[^a-z0-9_]/g, "_") || "salon")}${byobRegionPreset.usernameCountrySuffix ? `_${byobRegionPreset.usernameCountrySuffix}` : ""}_os_bot`
    .replace(/_+/g, "_")
    .slice(0, 32);
  const botFatherScript = `/newbot\n${botNameSuggestion}\n${botUsernameSuggestion}`;
  const autoCommandScript = [
    "/setcommands",
    botUsernameSuggestion,
    `dashboard - ${byobRegionPreset.commands.dashboard}`,
    `calendar - ${byobRegionPreset.commands.calendar}`,
    `clients - ${byobRegionPreset.commands.clients}`,
  ].join("\n");
  const effectiveCommandScript = commandScriptOverride.trim() ? commandScriptOverride : autoCommandScript;
  const effectiveMenuTargetUrl = menuTargetOverride.trim() ? menuTargetOverride : byobMenuTargetDefault;
  const byobSteps: ByobUiStep[] = [
    { id: "create", label: "Create bot in BotFather", done: !!byobToken.trim() },
    { id: "validate", label: "Validate token", done: byobValidated, active: byobPhase === "validating" },
    { id: "integration", label: "Create integration", done: !!byobIntegrationId, active: byobPhase === "creating" },
    { id: "webhook", label: "Sync webhook", done: byobWebhookSynced, active: byobPhase === "webhook" },
    { id: "test", label: "Send test message (optional)", done: byobTestSent || !byobChatId.trim(), active: byobPhase === "test" },
  ] as const;
  const botFatherSteps = [
    { id: "opened_botfather", label: "Open BotFather", done: botFatherChecklist.opened_botfather },
    { id: "copied_newbot_script", label: "Copy /newbot script", done: botFatherChecklist.copied_newbot_script },
    { id: "copied_commands_script", label: "Copy /setcommands script", done: botFatherChecklist.copied_commands_script },
    { id: "copied_menu_target", label: "Copy menu button target", done: botFatherChecklist.copied_menu_target },
  ] as const;
  const botFatherCompletedCount = botFatherSteps.filter((step) => step.done).length;
  const applyRegionalByobDefaults = () => {
    setCommandScriptOverride("");
    setMenuTargetOverride("");
    pushByobHistory("idle", `Applied regional BYOB helper defaults for ${byobRegionPreset.label} (${byobCountryCode})`);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="mx-auto max-w-3xl panel p-6 grid gap-4">
        <h1 className="text-2xl font-semibold">Onboarding Wizard</h1>
        <p className="text-sm text-slate-600">Telegram Mini App exchange or fallback signup session to tenant creation, then optional channel setup helpers.</p>

        {!exchange ? (
          <div className="grid gap-3">
            <textarea className="input min-h-32" placeholder="Telegram WebApp initData (auto-filled in Telegram Mini App)" value={initData} onChange={(e) => setInitData(e.target.value)} />
            <div className="grid md:grid-cols-3 gap-2">
              <input className="input" value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} placeholder="Country code" />
              <input className="input" value={locale} onChange={(e) => setLocale(e.target.value.toLowerCase())} placeholder="Locale" />
              <input className="input" value={captchaToken} onChange={(e) => setCaptchaToken(e.target.value)} placeholder="CAPTCHA token (optional)" />
            </div>
            <button className="btn" disabled={!initData.trim() || busy} onClick={runExchange}>{busy ? "Exchanging..." : "Verify Telegram & Continue"}</button>
          </div>
        ) : (
          <form className="grid gap-3" onSubmit={completeSignup}>
            <div className="rounded border border-slate-200 bg-white p-3 text-sm">
              <p><strong>Preset:</strong> {exchange.preset.country_code} / {exchange.preset.default_currency} / {exchange.preset.default_channel_strategy}</p>
            </div>
            <input className="input" placeholder="Salon name" value={tenantName} onChange={(e) => setTenantName(e.target.value)} required />
            <input className="input" placeholder="Slug (optional)" value={tenantSlug} onChange={(e) => setTenantSlug(e.target.value)} />
            <div className="grid md:grid-cols-2 gap-2">
              <input className="input" placeholder="Default branch name" value={branchName} onChange={(e) => setBranchName(e.target.value)} required />
              <input className="input" placeholder="Timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)} required />
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              <input className="input" placeholder="Currency" value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} required />
              <select className="input" value={channelStrategy} onChange={(e) => setChannelStrategy(e.target.value as any)}>
                {allowedStrategies.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <label className="flex gap-2 text-sm">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>I accept Terms and Privacy Policy</span>
            </label>
            <button className="btn" type="submit" disabled={busy || !tenantName.trim() || !branchName.trim() || !consent}>
              {busy ? "Creating tenant..." : "Create Tenant & Sign In"}
            </button>
          </form>
        )}

        {result ? (
          <div className="grid gap-3 rounded border border-emerald-200 bg-emerald-50 p-4 text-sm">
            <div>
              <p className="font-semibold">Signup completed</p>
              <p>Booking URL: {result.public_booking_url}</p>
              <p>Checklist: {result.onboarding_checklist.join(", ")}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button className="btn" onClick={() => navigate("/app/dashboard")}>Open Manager Dashboard</button>
              <a className="btn secondary" href={result.manager_app_url}>Open Manager URL</a>
            </div>

            <div className="rounded border border-amber-200 bg-white p-3 grid gap-2">
              <p className="font-medium">Optional: Bring Your Own Telegram Bot (BYOB)</p>
              <p className="text-xs text-slate-600">BotFather cannot create bots via API. This wizard automates everything after you paste the token (validation, integration creation, webhook sync, optional test send).</p>
              {copyFeedback ? <p className="text-xs text-emerald-700">{copyFeedback}</p> : null}

              <div className="rounded border border-sky-200 bg-sky-50 p-3 grid gap-2 text-xs">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-medium text-sky-900">
                    Regional BYOB Preset: {byobRegionPreset.label} ({byobCountryCode})
                  </p>
                  <button className="btn secondary" type="button" onClick={applyRegionalByobDefaults}>
                    Apply Regional Defaults
                  </button>
                </div>
                <p className="text-slate-700">{byobRegionPreset.botFatherLanguageHint}</p>
                <p className="text-slate-700">{byobRegionPreset.menuButtonHint}</p>
                <div className="grid gap-1">
                  {byobRegionPreset.setupNotes.map((note, idx) => (
                    <p key={`${byobCountryCode}-note-${idx}`} className="text-slate-700">
                      {idx + 1}. {note}
                    </p>
                  ))}
                </div>
              </div>

              <div className="rounded border border-slate-200 bg-slate-50 p-3 grid gap-2">
                <p className="text-xs font-medium uppercase tracking-[0.12em] text-slate-600">Step 1 - BotFather</p>
                <div className="rounded border border-slate-200 bg-white p-2 text-xs">
                  <p className="font-medium text-slate-700">Checklist Progress: {botFatherCompletedCount}/{botFatherSteps.length}</p>
                  <div className="mt-2 grid gap-1">
                    {botFatherSteps.map((step, index) => (
                      <label key={step.id} className="flex items-center justify-between gap-2 rounded border border-slate-200 px-2 py-1">
                        <span>{index + 1}. {step.label}</span>
                        <input
                          type="checkbox"
                          checked={step.done}
                          onChange={(e) => markBotFatherStep(step.id as keyof BotFatherChecklist, e.target.checked)}
                        />
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    className="btn secondary"
                    href={botFatherDeepLink}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => markBotFatherStep("opened_botfather")}
                  >
                    Open BotFather
                  </a>
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={() => copyWithFeedback(botFatherScript, () => markBotFatherStep("copied_newbot_script"))}
                  >
                    Copy /newbot Script
                  </button>
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={() => copyWithFeedback(effectiveCommandScript, () => markBotFatherStep("copied_commands_script"))}
                  >
                    Copy /setcommands Script
                  </button>
                </div>
                <div className="grid md:grid-cols-2 gap-2 text-xs">
                  <div className="rounded border border-slate-200 bg-white p-2">
                    <p className="font-medium text-slate-700">Suggested bot name</p>
                    <code className="break-all">{botNameSuggestion}</code>
                  </div>
                  <div className="rounded border border-slate-200 bg-white p-2">
                    <p className="font-medium text-slate-700">Suggested username</p>
                    <code className="break-all">{botUsernameSuggestion}</code>
                  </div>
                </div>
                <div className="rounded border border-slate-200 bg-white p-2 text-xs grid gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-slate-700">Editable /setcommands Script (auto-filled)</p>
                    <button className="btn secondary" type="button" onClick={() => setCommandScriptOverride("")}>Reset Auto-Fill</button>
                  </div>
                  <p className="text-slate-500">
                    Regional default command descriptions are generated from the selected country preset ({byobCountryCode}); edit before copying if needed.
                  </p>
                  <textarea
                    className="input min-h-28 font-mono text-xs"
                    value={effectiveCommandScript}
                    onChange={(e) => setCommandScriptOverride(e.target.value)}
                  />
                </div>
                <div className="rounded border border-slate-200 bg-white p-2 text-xs">
                  <p className="font-medium text-slate-700">Menu button target (for BotFather /setmenubutton)</p>
                  <div className="grid gap-2 mt-1">
                    <input className="input text-xs" value={effectiveMenuTargetUrl} onChange={(e) => setMenuTargetOverride(e.target.value)} />
                    <div className="flex flex-wrap gap-2">
                      <button
                        className="btn secondary"
                        type="button"
                        onClick={() => copyWithFeedback(effectiveMenuTargetUrl, () => markBotFatherStep("copied_menu_target"))}
                      >
                        Copy Menu URL
                      </button>
                      <button className="btn secondary" type="button" onClick={() => setMenuTargetOverride("")}>Reset Menu URL</button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded border border-slate-200 bg-slate-50 p-3 grid gap-2">
                <p className="text-xs font-medium uppercase tracking-[0.12em] text-slate-600">Step 2 - Paste Token + Auto-Configure</p>
                <a className="btn secondary" href={botFatherDeepLink} target="_blank" rel="noreferrer">Open BotFather</a>
                <p className="text-xs text-slate-600">
                  Regional test-send message: <code>{byobRegionPreset.testMessage}</code>
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                <input className="input" placeholder="Bot token" value={byobToken} onChange={(e) => setByobToken(e.target.value)} />
                <input className="input" placeholder="Owner Telegram chat_id (optional test send)" value={byobChatId} onChange={(e) => setByobChatId(e.target.value)} />
              </div>
              <button className="btn" type="button" onClick={createByobIntegration} disabled={!byobToken.trim() || byobPhase === "validating" || byobPhase === "creating" || byobPhase === "webhook" || byobPhase === "test"}>
                {byobPhase === "idle" || byobPhase === "error" ? "Run Auto-Setup" : byobPhase === "done" ? "Re-run Auto-Setup" : "Running Auto-Setup..."}
              </button>
              <div className="grid gap-1 text-xs">
                {byobSteps.map((step, index) => (
                  <div key={step.id} className="flex items-center justify-between rounded border border-slate-200 bg-white px-2 py-1">
                    <span>{index + 1}. {step.label}</span>
                    <span className={step.done ? "text-emerald-700" : step.active ? "text-amber-700" : "text-slate-500"}>
                      {step.done ? "done" : step.active ? "running" : "pending"}
                    </span>
                  </div>
                ))}
              </div>
              <div className="rounded border border-slate-200 bg-slate-50 p-2 text-xs">
                <p className="font-medium text-slate-700">BYOB Activity History</p>
                {byobHistory.length ? (
                  <div className="mt-2 grid gap-1">
                    {byobHistory.map((entry, idx) => (
                      <div key={`${entry.at}-${idx}`} className="rounded border border-slate-200 bg-white px-2 py-1">
                        <span className="text-slate-500">{new Date(entry.at).toLocaleTimeString()}</span>{" "}
                        <span className="font-medium">[{entry.phase}]</span>{" "}
                        <span>{entry.message}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1 text-slate-500">No BYOB actions run yet.</p>
                )}
              </div>
              {byobIntegrationId ? <p className="text-xs text-slate-600">Integration ID: <code>{byobIntegrationId}</code></p> : null}
              {byobStatus ? <p className="text-xs text-slate-700">{byobStatus}</p> : null}
            </div>

            <div className="rounded border border-slate-200 bg-white p-3 grid md:grid-cols-[1fr_auto] gap-3 items-center">
              <div>
                <p className="font-medium">QR Onboarding Entry</p>
                <p className="text-xs text-slate-600">Use this QR for in-person marketing to open the `/signup` entry flow.</p>
              </div>
              <QrCodeImage value={signupQrValue} alt="Signup QR" size={160} className="w-24 h-24 rounded border border-slate-200 p-1 bg-white" />
            </div>
          </div>
        ) : null}

        {error ? <p className="text-sm text-rose-600">{error}</p> : null}
      </div>
    </div>
  );
}
